import {Kontekst} from "felles/tjenester/Kontekst";
import {l} from "types/typescriptdefinisjoner_lederplan";
import _ from "lodash";
import {SakRessurs, Saksstatistikk, Sakstidspunkt} from "felles/tjenester/ressurser/SakRessurs";
import {Debugger} from "debug";
import {MøteInfo} from "./MøterIPlanModell";
import {gruppefarge, Gruppefarge} from "felles-ng2/modell/Gruppefarge";
import TilgangsrolleMedlem = l.TilgangsrolleMedlem;
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug: Debugger = require('debug')('SakerIPlanModell');

export class SakerIPlanModell {

    //konstante verdier som bestemmer bredder på labels- og møter-kontainer
    private readonly sakshøyde = 40;
    private readonly sakshøydeKomprimert = 20;
    private readonly saksVertikalMargin = 5;


    grupperinfo: Gruppeinfo[] = [];
    valgtSaksgruppeInfo: SaksgruppeInfo = null;
    alleGrupperingerCache: { [gruppeId: string]: GrupperingsEntitet[] } = {};

    private sakstidspunkterCache: { [sakId: string]: Sakstidspunkt };
    private benyttetSakshøyde = this.sakshøyde; //settes av initBenyttetSakshøyde()


    constructor(private readonly grupper: l.EnkelGruppeModell[],
                /** angir hvilken dimensjon saker skal grupperes etter ved visning av saker */
                private gruppering: l.SaksgrupperingPlan,
                /** hvorvidt saksdetaljer skal vises under saker (feks ant dok, oppgaver etc)*/
                visSaksdetaljer: boolean,
                private readonly møteInfoForMøte: (møteId: string) => MøteInfo,
                private readonly kontekst: Kontekst) {
        this.initBenyttetSakshøyde(visSaksdetaljer);
    }

    inneholderGruppe(gruppeId: string) {
        return this.finnGruppeinfoFor(gruppeId) != null;
    }

    alleGrupperingerVises(gruppeId: string) {
        return this.alleGrupperingerCache[gruppeId] != null;
    }

    ekspanderteGrupper(): string[] {
        return this.grupperinfo
            .filter(gruppe => gruppe.viserSaker)
            .map(gruppe => gruppe.gruppe.id);
    }

    initGrupper() {
        this.grupperinfo = this.grupper
            .map(gruppe => new Gruppeinfo(gruppe, this.kontekst));
    }

    grupperLagtTil(nyeGrupper: l.EnkelGruppeModell[]) {
        nyeGrupper
            .filter(gruppe => !this.inneholderGruppe(gruppe.id))
            .forEach(gruppe => this.grupperinfo.push(new Gruppeinfo(gruppe, this.kontekst)))
    }

    grupperFjernet(fjernedeGrupper: string[]) {
        _.remove(this.grupperinfo, info => _.includes(fjernedeGrupper, info.gruppe.id));
    }

    initMøter(møter: l.MøteModellEnkel[]) {
        this.grupperinfo.forEach(gruppe => gruppe.initAntallMoeter(møter));
    }

    initDetaljerForGruppe(gruppeId: string, møter: l.MøteModell[]) {
        const info = this.finnGruppeinfoFor(gruppeId);
        if (info == null) return;

        info.viserSaker = true
        info.saksgrupperinger = this.beregnSaksgruppeInfo(møter, gruppeId);
        info.møterMedSaker = møter;
    }

    /** reberegner detaljer for grupper vi tidligere har beregnet detaljer for */
    reberegnDetaljerForGrupper() {
        this.grupperinfo
            .filter(gruppe => gruppe.saksgrupperinger != null)
            .forEach(gruppe => this.reberegnDetaljerForGruppe(gruppe.gruppe.id));
    }

    reberegnDetaljerForGruppe(gruppeId: string) {
        const gruppeinfo = this.finnGruppeinfoFor(gruppeId);
        gruppeinfo.saksgrupperinger = this.beregnSaksgruppeInfo(gruppeinfo.møterMedSaker, gruppeId);
    }

    visAlleGrupperinger(gruppeId: string, grupperinger: GrupperingsEntitet[]) {
        this.alleGrupperingerCache[gruppeId] = grupperinger;
        this.reberegnDetaljerForGruppe(gruppeId);
    }

    nullstillAlleGrupperingerCache() {
        this.alleGrupperingerCache = {};
    }

    oppdaterValg(gruppering: l.SaksgrupperingPlan, visSaksdetaljer: boolean) {
        this.gruppering = gruppering;

        this.initBenyttetSakshøyde(visSaksdetaljer);
        this.reberegnDetaljerForGrupper();
    }

    aktiverSakslisteVisningsmodus(gruppeId: string, grupperingsentitetId: string): boolean {
        const info = this.finnGruppeinfoFor(gruppeId);
        this.valgtSaksgruppeInfo = _(info.saksgrupperinger).find(saksgruppe => saksgruppe.saksgruppeId === grupperingsentitetId)
        //gå til matrisevisningsmodus dersom vi ikke finner forespurt saksgruppe
        if (this.valgtSaksgruppeInfo == null) return false;
        return true;
    }

    deaktiverSakslisteVisningsmodus() {
        this.valgtSaksgruppeInfo = null;
    }

    valgtSaksgruppeInfoGruppeInfo() {
        if (erNil(this.valgtSaksgruppeInfo)) return null;
        return this.grupper.find(gruppe => gruppe.id == this.valgtSaksgruppeInfo.gruppeId);
    }

    // hjelpemetoder

    private initBenyttetSakshøyde(visSaksdetaljer: boolean) {
        this.benyttetSakshøyde = visSaksdetaljer ? this.sakshøyde : this.sakshøydeKomprimert;
    }

    private finnGruppeinfoFor(gruppeId: string): Gruppeinfo {
        return _.find(this.grupperinfo, info => info.gruppe.id == gruppeId);
    }

    private beregnSaksgruppeInfo(møter: l.MøteModell[], gruppeId: string): SaksgruppeInfo[] {
        debug("beregnDetaljerForGruppe", gruppeId)
        this.sakstidspunkterCache = _(møter)
            .map(møte => SakRessurs.sakstidspunkter(møte))
            .reduce(_.merge);

        let saksgrupper: SaksgruppeInfo[] = _(møter)
            //som array av saker på tvers av møter
            .flatMap(m => m.saker)
            //fjern pausesaker
            .filter(s => !s.erPause)
            //gruppert på tema/sakstype/ansvarlig
            .groupBy(sak => this.saksgruppeNøkkel(sak))
            //lag gruppeInfo representasjon for hver gruppe (tema/sakstype/ansvarlig)
            .mapValues((saker, gruppeNøkkel) => this.somSaksgruppeInfo(saker, gruppeId, gruppeNøkkel))
            .values()
            .value();

        saksgrupper = this.leggTilUbrukteGrupperinger(saksgrupper, gruppeId);
        saksgrupper = this.sorterGrupperinger(saksgrupper);
        return saksgrupper;
    }

    private somSaksgruppeInfo(saker: l.AgendaelementModell[], gruppeId: string, gruppeNøkkel: string) {
        const møteInfos: MøteMedSakerInfo[] = _(saker)
            //gruppert på møte
            .groupBy(s => s.moete.id)
            //lag møteDetaljerInfo representasjon for hver møte
            .mapValues((saker: l.AgendaelementModell[], møteId: string) => this.somMøteMedSakerInfo(møteId, saker))
            .values()
            .value();

        var maxAntSakerPerMoete = _(møteInfos).map((møte: MøteMedSakerInfo) => møte.saker.length).max();
        var totAntSaker = _(møteInfos).map((møte: MøteMedSakerInfo) => møte.saker.length).sum();
        const saksgruppe = this.saksgruppeVerdi(møteInfos[0].saker[0].sak);

        return <SaksgruppeInfo>{
            gruppeId: gruppeId,
            saksgruppeType: this.gruppering,
            saksgruppeId: gruppeNøkkel, //se this.saksgruppeNøkkel(sak)
            saksgruppe: saksgruppe,
            maxAntSakerPerMoete: maxAntSakerPerMoete,
            totAntSaker: totAntSaker,
            pos: {hoyde: (this.benyttetSakshøyde + (this.saksVertikalMargin * 2)) * maxAntSakerPerMoete},
            moeter: møteInfos.filter(møteInfo => møteInfo.moeteInfo != null) //dersom planen er trunkert vil moeteInfo være null - fjerner disse MøteMedSakerInfo forekomstene da disse møtene ikke skal vises
        }
    }

    private somMøteMedSakerInfo(møteId: string, saker: l.AgendaelementModell[]): MøteMedSakerInfo {
        return {
            moeteId: møteId,
            moeteInfo: this.møteInfoForMøte(møteId),
            //lag sakInfo representasjon for hver sak
            saker: saker
                .map((sak: l.AgendaelementModell, index: number) => {
                    const tl = index == 0 ? this.saksVertikalMargin :
                        (this.benyttetSakshøyde * index) + ((this.saksVertikalMargin * 2) * index) + this.saksVertikalMargin;
                    return this.somSakInfo(sak, tl)
                })
        };
    }

    private somSakInfo(sak: l.AgendaelementModell, tl: number): SakInfo {
        return {
            pos: {
                tl: tl,
                bl: tl + this.benyttetSakshøyde,
                hoyde: this.benyttetSakshøyde
            },
            statistikk: SakRessurs.saksstatistikk(sak),
            sakstidspunkt: this.sakstidspunkterCache[sak.id],
            sak: sak
        };
    }

    private saksgruppeNøkkel(sak: l.AgendaelementModell): string {
        switch (this.gruppering) {
            case l.SaksgrupperingPlan.sakstype:
                return sak.sakstype ? sak.sakstype.id : 'uten_sakstype';
            case l.SaksgrupperingPlan.ansvarlig:
                return sak.ansvarlig ? sak.ansvarlig.id : 'uten_ansvarlig';
            case l.SaksgrupperingPlan.tema:
                return sak.tema ? sak.tema.id : 'uten_tema';
        }
    }

    private saksgruppeVerdi(sak: l.AgendaelementModell): GrupperingsEntitet {
        switch (this.gruppering) {
            case l.SaksgrupperingPlan.sakstype:
                return sak.sakstype;
            case l.SaksgrupperingPlan.ansvarlig:
                return sak.ansvarlig;
            case l.SaksgrupperingPlan.tema:
                return sak.tema;
        }
    }

    private leggTilUbrukteGrupperinger(saksgrupper: SaksgruppeInfo[], gruppeId: string): SaksgruppeInfo[] {
        const alleGrupperinger = this.alleGrupperingerCache[gruppeId];
        if (alleGrupperinger == null) return saksgrupper;

        //finn SaksgruppeInfo for alle eksisterende grupperinger
        let alleSaksgrupper = alleGrupperinger
            .map(gruppering => {
                return _(saksgrupper).find((gruppe: SaksgruppeInfo) => gruppe.saksgruppeId === gruppering.id) ||
                    <SaksgruppeInfo>{
                        gruppeId: gruppeId,
                        saksgruppeType: this.gruppering,
                        saksgruppeId: gruppering.id,
                        saksgruppe: gruppering,
                        moeter: [],
                        maxAntSakerPerMoete: 0,
                        totAntSaker: 0,
                        pos: null
                    }
            })

        //tilfelle eksisterende grupperinger inneholder grupper som ikke finnes i alleGrupperinger (feks gruppen "tema ikke satt")
        alleSaksgrupper = _.unionBy(alleSaksgrupper, saksgrupper, (gruppe: SaksgruppeInfo) => gruppe.saksgruppeId)

        return alleSaksgrupper;
    }

    private sorterGrupperinger(saksgrupper: SaksgruppeInfo[]): SaksgruppeInfo[] {
        switch (this.gruppering) {
            case l.SaksgrupperingPlan.tema:
                return saksgrupper.sort((a, b) =>
                    this.sammenliknOptTall((<l.TemaModell>a.saksgruppe)?.indeks, (<l.TemaModell>b.saksgruppe)?.indeks));
            case l.SaksgrupperingPlan.sakstype:
                return saksgrupper.sort((a, b) =>
                    this.sammenliknOptTall((<l.SakstypeModell>a.saksgruppe)?.indeks, (<l.SakstypeModell>b.saksgruppe)?.indeks));
            case l.SaksgrupperingPlan.ansvarlig:
                return saksgrupper.sort((a, b) =>
                    this.sammenliknOptTekst((<l.GruppeMedlemListeModell>a.saksgruppe)?.navn, (<l.GruppeMedlemListeModell>b.saksgruppe)?.navn));
        }
        return saksgrupper;
    }

    private sammenliknOptTall(a: number, b: number) {
        if (!_.isNumber(a)) return 1;
        if (!_.isNumber(b)) return -1;
        return a - b;
    }

    private sammenliknOptTekst(a: string, b: string) {
        if (_.isNil(a)) return 1;
        if (_.isNil(b)) return -1;
        return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
    }

}

export class Gruppeinfo {
    gruppe: l.EnkelGruppeModell;
    antallMoeter = 0;
    brukerHarSkrivetilgang: boolean;
    farger: Gruppefarge;
    viserSaker = false;
    /** populeres når man viser detaljer for en gruppe (dvs ekspanderer gruppen for å se sakene) */
    saksgrupperinger: SaksgruppeInfo[] | null;
    /** populeres når man viser detaljer for en gruppe (dvs ekspanderer gruppen for å se sakene) */
    møterMedSaker: l.MøteModell[];

    constructor(gruppe: l.EnkelGruppeModell, kontekst: Kontekst) {
        this.gruppe = gruppe;
        this.brukerHarSkrivetilgang = kontekst.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, gruppe.id);
        this.farger = gruppefarge(gruppe);
    }

    initAntallMoeter(allemøter: l.MøteModellEnkel[]) {
        this.antallMoeter = allemøter.filter(m => m.eiendeGruppeId === this.gruppe.id).length;
    }
}

export type SaksgruppeType = 'tema' | 'sakstype' | 'ansvarlig';

export interface SaksgruppeInfo {
    gruppeId: string;
    saksgruppeType: SaksgruppeType;
    saksgruppeId: string;
    saksgruppe: GrupperingsEntitet;
    maxAntSakerPerMoete: number;
    totAntSaker: number;
    pos: { hoyde: number; };
    moeter: MøteMedSakerInfo[];
}

export interface MøteMedSakerInfo {
    moeteId: string;
    moeteInfo: MøteInfo;
    saker: SakInfo[];
}

export interface SakInfo {
    pos: TlBlPosisjon;
    statistikk: Saksstatistikk;
    sakstidspunkt: Sakstidspunkt;
    sak: l.AgendaelementModell;
}

export interface TlBlPosisjon {
    tl: number;
    bl: number;
    hoyde: number;
}

export type GrupperingsEntitet = l.TemaModell | l.SakstypeModell | l.GruppeMedlemListeModell;
